import { isNil, toNumber } from "lodash-es"

import { downloadBlobFile } from "@/utils/downloadBlobFile"
import request, { formatRequest, SERVICE_SUCCESS_CODE } from "@/utils/request"
import RequestCancel from "@/utils/request-cancel"

import { ApiResult } from "../types"
import {
  AvailablePickupDateData,
  ChangeOrderAddressProps,
  ChoicesCount,
  GetRequestIdListParams,
  GetRequestIdListResult,
  IApproveProps,
  IManagementConfig,
  IReceivedProps,
  IRefundProps,
  IReturnItem,
  IShoppoData,
  ManagementListQueryParams,
  RecognizeLogisticsParams,
  RecognizeLogisticsResult,
  ResendNotificationParams,
  TableColumnData,
} from "./type"

const toApproveNumber = (value: unknown): number | undefined => {
  if (isNil(value) || value === "") {
    return undefined
  }
  const num = typeof value === "number" ? value : toNumber(value)
  return Number.isNaN(num) ? undefined : num
}

export const normalizeApproveParams = <T>(params: T): T => {
  if (!params || typeof params !== "object") {
    return params
  }

  const source = params as Record<string, unknown>
  const normalized: Record<string, unknown> = { ...source }

  const returnAddressId = toApproveNumber(normalized.return_address_id)
  if (returnAddressId !== undefined) {
    normalized.return_address_id = returnAddressId
  }

  const logisticsId = toApproveNumber(normalized.logistics_id)
  if (logisticsId !== undefined) {
    normalized.logistics_id = logisticsId
  }

  const packageId = toApproveNumber(normalized.package_id)
  if (packageId !== undefined) {
    normalized.package_id = packageId
  }

  const weight = toApproveNumber(normalized.weight)
  if (weight !== undefined) {
    normalized.weight = weight
  }

  if (Array.isArray(normalized.label_list)) {
    normalized.label_list = normalized.label_list.map((labelItem) => {
      if (!labelItem || typeof labelItem !== "object") {
        return labelItem
      }
      const nextLabelItem = {
        ...(labelItem as Record<string, unknown>),
      }

      const isLabelUrl = toApproveNumber(nextLabelItem.is_label_url)
      if (isLabelUrl !== undefined) {
        nextLabelItem.is_label_url = isLabelUrl
      }

      if (
        nextLabelItem.return_label_file &&
        typeof nextLabelItem.return_label_file === "object"
      ) {
        const returnLabelFile = {
          ...(nextLabelItem.return_label_file as Record<string, unknown>),
        }
        const returnLabelFileSize = toApproveNumber(returnLabelFile.size)
        if (returnLabelFileSize !== undefined) {
          returnLabelFile.size = returnLabelFileSize
        }
        nextLabelItem.return_label_file = returnLabelFile
      }

      return nextLabelItem
    })
  }

  const other = normalized.other as
    | {
        weight?: unknown
        label_dhl_express?: { estimated_days?: unknown; [key: string]: unknown }
        productInfo?: {
          weight?: unknown
          list?: unknown[]
          [key: string]: unknown
        }
        [key: string]: unknown
      }
    | undefined

  if (other && typeof other === "object") {
    const nextOther: Record<string, unknown> = { ...other }

    const otherWeight = toApproveNumber(other.weight)
    if (otherWeight !== undefined) {
      nextOther.weight = otherWeight
    }

    const labelDhlExpress = other.label_dhl_express
    if (labelDhlExpress && typeof labelDhlExpress === "object") {
      const nextLabelDhlExpress = {
        ...labelDhlExpress,
      } as Record<string, unknown>
      const estimatedDays = toApproveNumber(nextLabelDhlExpress.estimated_days)
      if (estimatedDays !== undefined) {
        nextLabelDhlExpress.estimated_days = estimatedDays
      }
      nextOther.label_dhl_express = nextLabelDhlExpress
    }

    const productInfo = other.productInfo
    if (productInfo && typeof productInfo === "object") {
      const nextProductInfo = {
        ...productInfo,
      } as Record<string, unknown>
      const productWeight = toApproveNumber(nextProductInfo.weight)
      if (productWeight !== undefined) {
        nextProductInfo.weight = productWeight
      }

      if (Array.isArray(nextProductInfo.list)) {
        nextProductInfo.list = nextProductInfo.list.map((productItem) => {
          if (!productItem || typeof productItem !== "object") {
            return productItem
          }
          const nextProductItem = {
            ...(productItem as Record<string, unknown>),
          }

          const returnProductId = toApproveNumber(
            nextProductItem.return_product_id,
          )
          if (returnProductId !== undefined) {
            nextProductItem.return_product_id = returnProductId
          }

          const netWeight = toApproveNumber(nextProductItem.net_weight)
          if (netWeight !== undefined) {
            nextProductItem.net_weight = netWeight
          }

          return nextProductItem
        })
      }

      nextOther.productInfo = nextProductInfo
    }

    normalized.other = nextOther
  }

  return normalized as T
}

const APIManagement = {
  /**
   * 获取订阅信息列表
   * @param {IManagementConfig} params 入参
   */
  list(params?: Partial<IManagementConfig>) {
    const url: string = "/v1/management"

    RequestCancel.pop(url)

    return request.get<IReturnItem>(url, {
      cancelToken: RequestCancel.push(url),
      params,
    })
  },

  /**
   * 导出CSV表格
   * @param {IManagementConfig} params 入参
   */
  exportCSV(params?: Partial<IManagementConfig>) {
    const url: string = "/v1/management"

    RequestCancel.pop(url)

    return request.get<IReturnItem>(url, {
      cancelToken: RequestCancel.push(url),
      params,
      responseType: "blob",
    })
  },

  /**
   * @param {number} id 订单id
   */
  getOrderDetail(id: number, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/show"
      : `/v1/management/${id}`

    return formatRequest(
      request.get(requestUrl, scenario ? { params: { scenario } } : undefined),
      { hideMsg: true },
    )
  },

  getAvailablePickupDate(params: { time_zone: string }) {
    return formatRequest<AvailablePickupDateData>(
      request.get("/go-api/v1/management/available_pickup_date", { params }),
      { hideMsg: true },
    )
  },

  resetSandboxOrder(params: { scenario: string }) {
    return formatRequest(
      request.post("/go-api/v1/management/sandbox/reset", params),
      {
        hideMsg: true,
      },
    )
  },

  resendNotification(params: ResendNotificationParams, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/resendNotification"
      : "/go-api/v1/management/resend_notification"

    const requestData = scenario ? { scenario, ...params } : params
    return formatRequest(request.post(requestUrl, requestData), {
      success: {
        [SERVICE_SUCCESS_CODE]: t("ResentSuccessfully"),
      },
      error: {
        "*": t("ResendFailed"),
      },
    })
  },

  getRequestIdList(params: GetRequestIdListParams) {
    return formatRequest(
      request.get<ApiResult<GetRequestIdListResult>>("/v1/getRequestIdList", {
        params,
      }),
    )
  },

  /**
   * @description 发送同意请求
   */
  approveRequest(params: IApproveProps) {
    return formatRequest(
      request.post(
        "/go-api/v1/management/approve",
        normalizeApproveParams(params),
        {
          timeout: 90000,
        },
      ),
      {
        success: {
          1000: t("ApprovedSuccessfully"),
        },
        error: {
          duration: 15 * 1000,
        },
      },
    )
  },

  /**
   * @description 发送同意请求
   */
  getHsCodeList(data: { search: string }) {
    return formatRequest(request.post("/go-api/v1/com/hsCode/list", data))
  },

  recognizeLogistics(params: RecognizeLogisticsParams) {
    return formatRequest(
      request.post<ApiResult<RecognizeLogisticsResult>>(
        "/go-api/v1/management/recognize_logistics",
        params,
      ),
    )
  },

  /**
   * @description 发送同意请求
   */
  approveRequestNew(params: IApproveProps, scenario?: string) {
    const data = normalizeApproveParams(params)

    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/approve"
      : "/go-api/v1/management/approve"
    const requestData = scenario ? { ...data, scenario } : data

    return formatRequest(
      request.post(requestUrl, requestData, {
        timeout: 90000,
      }),
      {
        success: {
          1000: t("ApprovedSuccessfully"),
        },
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },

  /**
   * @description 发送同意请求 点击了稍后同意
   */
  approveRequestLater(params, scenario?: string) {
    const data = normalizeApproveParams(params)

    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/approve"
      : "/v1/updateShipping"
    const requestData = scenario ? { ...data, scenario } : data

    return formatRequest(
      request.post(requestUrl, requestData, {
        timeout: 90000,
      }),
      {
        success: {
          1000: t("ApprovedSuccessfully"),
        },
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },
  /**
   * @description 更新return label
   */
  updateReturnLabel(params: UpdateLabelParams, scenario?: string) {
    const data = normalizeApproveParams(params)

    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/updateLabel"
      : "/v1/updateLabel"
    const requestData = scenario ? { ...data, scenario } : data

    return formatRequest(
      request.post(requestUrl, requestData, {
        timeout: 90000,
      }),
      {
        success: {
          1000: t("LabelUpdatedSuccessfully"),
        },
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },

  /**
   * @description 创建createLabelByEasyship
   */
  createLabelByEasyship(params: UpdateLabelParams) {
    return formatRequest(
      request.post("/v1/createLabelByEasyship", params, { timeout: 90000 }),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },

  /**
   * @description 获取 rate 请求参数发货
   */
  getRateByDhlExpress(params: UpdateLabelParams) {
    return formatRequest(request.post("/v1/getRateByDhlExpress", params), {
      error: {
        duration: 10 * 1000,
      },
    })
  },

  /**
   * @description 拒绝退还请求
   */
  rejectedRequest(request_id: number, value: string, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/reject"
      : "/v1/reject"
    const requestData = scenario
      ? { scenario, reject_reason: value }
      : { request_id, decline_reason: value }

    return formatRequest(request.post(requestUrl, requestData), {
      success: {
        1000: t("RejectedSuccessfully"),
      },
    })
  },

  /**
   * @description 换货申请
   */
  exchangeRequest(id: number, orderNum: string) {
    return formatRequest(
      request.post("/go-api/v1/return/exchange", {
        request_id: id,
        order_num: orderNum,
      }),
    )
  },

  /**
   * @description 换其他货申请
   */
  exchangeOthersRequest(id: number, orderNum: string) {
    return formatRequest(
      request.post("/go-api/v1/return/exchange_other", {
        request_id: id,
        order_num: orderNum,
      }),
    )
  },

  /**
   * @description 标记为已收到
   */
  receivedRequest(params: IReceivedProps, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/received"
      : "/go-api/v1/management/received"
    const requestData = scenario ? { scenario, ...params } : params

    return formatRequest(request.post(requestUrl, requestData), {
      success: {
        1000: t("MarkedAsReceivedSuccessfully"),
      },
    })
  },

  /**
   * @description 标记为已解决
   */
  resolvedRequest(id: number, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/resolved"
      : "/v1/resolved"
    const requestData = scenario
      ? { scenario, request_id: id }
      : { request_id: id }
    return formatRequest(request.post(requestUrl, requestData), {
      success: {
        1000: t("MarkedAsResolvedSuccessfully"),
      },
    })
  },

  /**
   * @description 补货
   */
  restockRequest(params: any, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/restock"
      : "/go-api/v1/management/restock"
    const requestData = scenario ? { scenario, ...params } : params
    return formatRequest(request.post(requestUrl, requestData), {
      success: {
        1000: t("MarkedAsRestockedSuccessfully"),
      },
    })
  },

  /**
   * @description 退款
   */
  refundRequest(params: IRefundProps, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/refund"
      : "/go-api/v1/refund"

    return formatRequest(request.post(requestUrl, params), {
      success: {
        1000: t("RefundedSuccessfully"),
      },
    })
  },

  /**
   * @description 保存运费退款类型 refund_shipping_type 1-不退 2-全退
   */
  saveRefundShippingType(
    params: {
      request_id: number
      refund_shipping_type: number
    },
    scenario?: string,
  ) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/saveRefundShippingType"
      : "/go-api/v1/management/save_refund_shipping_type"
    const requestData = scenario ? { scenario, ...params } : params

    return formatRequest(request.post(requestUrl, requestData))
  },

  /**
   * @description 退款选退运费查询接口
   */
  getReturnMoney(
    params: {
      request_id: number | string
      full_refund: number
      line_items: IRefundProps["line_items"]
    },
    scenario?: string,
  ) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/getReturnMoney"
      : "/go-api/v1/getReturnMoney"
    const requestData = scenario ? { scenario, ...params } : params

    return formatRequest(
      request.post<ApiResult<IShoppoData>>(requestUrl, requestData),
      { hideMsg: true },
    )
  },

  getCarrierListNew(search?: string) {
    return formatRequest(
      request.get("/v1/shippingCarrier", {
        params: search,
      }),
    )
  },
  updateShippingCost(
    data: {
      request_id: number
      shipping_cost: number
    },
    scenario?: string,
  ) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/updateShippingCost"
      : "/go-api/v1/management/update_shipping_cost"
    const requestData = scenario ? { scenario, ...data } : data

    return formatRequest(request.post(requestUrl, requestData))
  },
  updateManagementWiseInfo(data: {
    /**
     * 国家
     */
    country: string
    /**
     * 货币
     */
    currency: string
    /**
     * 邮箱
     */
    email: string
    /**
     * 电话
     */
    phone?: string
    /**
     * request id
     */
    request_id: number
    /**
     * iban
     */
    wise_iban: string
    /**
     * wise name
     */
    wise_name: string
  }) {
    return formatRequest(
      request.post("/go-api/v2/setting/wise/customer/create", data),
      {
        showErrorMsg: true,
      },
    )
  },
  updateManagementBankAccountInfo(data: any) {
    return formatRequest(
      request.post("/go-api/v2/setting/bank/customer/create", data),
    )
  },

  // 获取当前labelFee数据
  getLableFeeInfo(dara: {
    shopify_order_id: number
    solution_id: number
    request_id: number
  }) {
    return formatRequest(
      request.get("/v1/getLabelFeeUseInfo", {
        params: dara,
      }),
    )
  },

  /**
   * @description 获取这个运输商的服务
   */
  getShippoServiceNew(params, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/getRateByShippo"
      : "/v1/getRateByShippo"

    return formatRequest(
      scenario
        ? request.get(requestUrl)
        : request.post<ApiResult<ShipInfo>>(requestUrl, params),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },

  /**
   * @description 获取这个运输商的服务
   */
  getRateByEasyship(params) {
    return formatRequest(
      request.post<ApiResult<ShipInfo>>("/v1/getRateByEasyship", params),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },

  getSendcloudServiceNew(params) {
    return formatRequest(
      request.post("/v1/getShippingMethodBySendCloudNew", params),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },
  getFedexServiceNew(params: {
    request_id: number
    return_address_id
    weight
    units
  }) {
    return formatRequest(
      request.get<ApiResult<FedexInfo>>("/v1/getRateByFedEx", {
        params,
      }),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },
  createNewReturn(params: { order_number: string; email: string }) {
    return request.get("/v1/checkOrder", {
      params,
    })
  },

  getShipStationDefaultConfig() {
    return formatRequest(request.get("/v1/getConfigByShipstation"), {
      error: {
        duration: 10 * 1000,
      },
    })
  },

  getDHLLocation() {
    return formatRequest(
      request.get<ApiResult<DHLInfo>>(
        "/go-api/v1/integration/GetLocationListByDhl",
      ),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },
  getDHLRate(params) {
    return formatRequest(
      request.post<ApiResult<DHLInfo>>(
        "/go-api/v1/integration/getRateByDhl",
        params,
      ),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },

  getSelectByDhlExpress() {
    return formatRequest(
      request.post<ApiResult<DHLInfo>>("/v1/getSelectByDhlExpress"),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },

  getShiprocketRate(params) {
    return formatRequest(request.post("/v1/getRateByShiprocket", params), {
      error: {
        duration: 10 * 1000,
      },
    })
  },

  getSendleRate(params) {
    return formatRequest(request.post("/v1/getRateBySendle", params), {
      error: {
        duration: 10 * 1000,
      },
    })
  },

  // getShipStationRate(params) {
  //   return formatRequest(request.get("/v1/getRateByShipstation", { params }))
  // },

  // 获取shipstation费率 V2 Copy
  getShipStationRateV2(params) {
    return formatRequest(request.post("/v1/getRateByShipstationV2", params))
  },

  saveTags(params: { tags: string[]; request_id: number }, scenario?: string) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/setReturnTag"
      : "/go-api/v1/management/setReturnTag"
    const requestData = scenario ? { scenario, ...params } : params

    return formatRequest(
      request.post<{ Tags?: string[] }>(requestUrl, requestData),
    )
  },

  approveWithoutMethod(params, scenario?: string) {
    const data = normalizeApproveParams(params)

    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/approveWithoutShippingMethod"
      : "/go-api/v1/management/approveWithoutShippingMethod"
    const requestData = scenario ? { ...data, scenario } : data

    return formatRequest(
      scenario
        ? request.post(requestUrl, requestData, { timeout: 90000 })
        : request.post(requestUrl, requestData),
      {
        success: {
          1000: t("ApprovedSuccessfully"),
        },
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },
  getUSPSService(params) {
    return formatRequest(
      request.post<ApiResult<ShipInfo>>("/v1/getRateByShippo", params),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },
  getRateByEasypost(params) {
    return formatRequest(
      request.post<ApiResult<ShipInfo>>("/v1/getRateByEasypost", params),
      {
        error: {
          duration: 10 * 1000,
        },
      },
    )
  },
  // 更新归档状态
  updateArchiveStatus(
    params: {
      request_id: number
      is_archive: number
    },
    scenario?: string,
  ) {
    const requestUrl = scenario
      ? "/go-api/v1/management/sandbox/archive/operate"
      : "/go-api/v1/management/archive/operate"
    const requestData = scenario ? { scenario, ...params } : params

    return formatRequest(request.post(requestUrl, requestData), {
      error: {
        duration: 10 * 1000,
      },
      success: {
        1000:
          params.is_archive === 1
            ? t("ArchivedSuccessfully")
            : t("UnarchivedSuccessfully"),
      },
    })
  },

  /**
   * @description 获取费率前相关验证逻辑
   */

  validateRate(params) {
    return formatRequest(request.post("/v1/validateRate", params), {
      hideMsg: true,
    })
  },

  /**
   * @description 更新Wise信息
   */
  updateWiseInfo(params: {
    request_id: number
    wise_name: string
    wise_iban: string
  }) {
    return formatRequest(
      request.post("/go-api/v1/management/update_wise_info", params),
      {
        success: {
          1000: t("SavedSuccessfully"),
        },
        error: {
          "*": t("SavedFailed"),
        },
      },
    )
  },

  // 修改地址
  setChangeOrderAddress(params: ChangeOrderAddressProps) {
    return formatRequest(
      request.post("/go-api/v1/addr/changeOrderAddress", params),
      {
        success: {
          1000: t("AddressUpdatedSuccessfully"),
        },
      },
    )
  },
  // 修改地址
  closeManagementBanner(params: any) {
    return formatRequest(
      request.post("/go-api/v1/management/close_banner", params),
      { hideMsg: true },
    )
  },

  // request_status数量
  getChoicesCount(
    type:
      | "request_status"
      | "reasons"
      | "tags"
      | "solutions"
      | "shipping_methods"
      | "shipping_status"
      | "received_restocked"
      | "resolution_status"
      | "destinations",
    params?: Partial<ManagementListQueryParams>,
    scenario?: string,
  ) {
    if (scenario && type === "tags") {
      return formatRequest<ChoicesCount[]>(
        request.get("/go-api/v1/management/sandbox/tags"),
      )
    }

    return formatRequest<ChoicesCount[]>(
      request.post(`/go-api/v1/management/${type}`, params ?? {}),
    )
  },

  // custom view 相关接口
  // 查询自定义视图 /go-api/v1/management/custom_views
  getCustomViews() {
    return formatRequest<
      {
        name: string
        filters: Record<string, any>
      }[]
    >(request.get("/go-api/v1/management/custom_views"))
  },

  // 更新 /go-api/v1/management/custom_views/upsert
  updateCustomView(data: {
    old_name: string
    name: string
    filters: Record<string, any>
  }) {
    return formatRequest(
      request.post("/go-api/v1/management/custom_views/upsert", data),
    )
  },

  // 删除 /go-api/v1/management/custom_views/delete
  deleteCustomView(params: { name: string }) {
    return formatRequest(
      request.post("/go-api/v1/management/custom_views/delete", params),
    )
  },

  // 自定义clumns和sort /go-api/v1/management/table_columns
  getTableColumns() {
    return formatRequest<TableColumnData>(
      request.get("/go-api/v1/management/table_columns"),
    )
  },

  // 更新自定义clumns和sort /go-api/v1/management/table_columns
  updateTableColumns(data: TableColumnData) {
    return formatRequest(
      request.post("/go-api/v1/management/table_columns/upsert", data),
      // {
      //   success: {
      //     1000: t("OperationSuccessful"),
      //   },
      // },
    )
  },

  // 批量拒绝统计数量 /v1/reject/batch/summary
  rejectRejectSummary(data: {
    is_all: boolean
    request_ids: string[]
    filters: Partial<ManagementListQueryParams>
  }) {
    return formatRequest<{
      selected_total: number
      forbid_total: number
      tips: {
        rma: string
        order_no: string
        order_link: string
      }[]
    }>(request.post("/v1/reject/batch/summary", data))
  },

  // 批量拒绝 /v1/reject/batch
  rejectReject(data: {
    is_all: boolean
    request_ids: string[]
    filters: Partial<ManagementListQueryParams>
    decline_reason: string
  }) {
    return formatRequest<{ queued_total: number }>(
      request.post("/v1/reject/batch", data),
    )
  },

  // 批量通过统计数量 /v1/approve/batch/summary
  approveSummary(data: {
    is_all: boolean
    request_ids: string[]
    filters: Partial<ManagementListQueryParams>
  }) {
    return formatRequest<{
      forbid_total: number
      selected_total: number
    }>(
      request.post(
        "/go-api/v1/management/approveWithoutShippingMethod/batch/summary",
        data,
      ),
    )
  },

  // 批量通过 /v1/approve/batch
  approveApprove(data: {
    is_all: boolean
    request_ids: string[]
    filters: Partial<ManagementListQueryParams>
  }) {
    return formatRequest<{ queued_total: number }>(
      request.post(
        "/go-api/v1/management/approveWithoutShippingMethod/batch",
        data,
      ),
    )
  },

  // 批量resolve 统计 /v1/resolved/batch/summary
  resolveSummary(data: {
    is_all: boolean
    request_ids: string[]
    filters: Partial<ManagementListQueryParams>
  }) {
    return formatRequest<{
      forbid_total: number
      selected_total: number
    }>(request.post("/v1/resolved/batch/summary", data))
  },

  // 批量resolved /v1/resolved/batch
  resolveResolve(data: {
    is_all: boolean
    request_ids: string[]
    filters: Partial<ManagementListQueryParams>
  }) {
    return formatRequest<{ queued_total: number }>(
      request.post("/v1/resolved/batch", data),
    )
  },

  // 导出统计数量 /v1/management/export/summary
  exportSummary(data: {
    is_all?: boolean
    filters?: Partial<ManagementListQueryParams>
  }) {
    return formatRequest<{
      default_email: string
      matched_total: number
      need_email: boolean
    }>(request.post("/v1/management/export/summary", data))
  },

  // 导出接口 /v1/management/export
  exportDataForEmail(data: {
    is_all?: boolean
    filters?: Partial<ManagementListQueryParams>
    email?: string
  }) {
    return formatRequest<{
      filename: string
      matched_total: number
      queued: boolean
    }>(request.post("/v1/management/export", data))
  },

  // 导出接口直接下载
  exportDataForDirectDownload(data: {
    is_all?: boolean
    filters?: Partial<ManagementListQueryParams>
  }) {
    const url: string = "/v1/management/export"

    RequestCancel.pop(url)

    return request.post(url, data, {
      cancelToken: RequestCancel.push(url),
      responseType: "blob",
    })
  },

  // 导出接口直接下载（带文件名解析）
  exportDataAndDownload(params?: {
    is_all?: boolean
    filters?: Partial<ManagementListQueryParams>
  }) {
    const url: string = "/v1/management/export"

    RequestCancel.pop(url)

    return request
      .post(url, params, {
        cancelToken: RequestCancel.push(url),
        responseType: "blob",
      })
      .then(async (response) => {
        const blob = response.data as Blob
        const contentDisposition = response.headers[
          "content-disposition"
        ] as string
        let filename = "parcelwill_returns_export.csv"

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
          }
        }

        await downloadBlobFile({ blob, filename })
        window.track("export_request")

        return { status: response.status, filename }
      })
  },
}

type ILabelList = {
  tracking_number: string
  shipping_carrier: string
  label_url: string
  is_label_url: number
  return_label_file: {
    type: string
    size: number
    name: string
  }
}

export interface UpdateLabelParams {
  /**
   * type 1 自动 2手动
   */
  type: number
  /**
   * request_id
   */
  request_id: number
  /**
   * 要修改的return label链接
   */
  return_label?: string
  /**
   * 运输商
   */
  shipping_carrier?: string
  /**
   * 查询单号
   */
  tracking_number?: string

  service_type?: string
  return_address_id?: string
  logistics_id?: number
  provider?: string
  units?: string
  weight?: number
  is_label_url?: number
  act_fee?: number // Shipping fee 金额
  return_label_file?: {
    type: string
    size: number
    name: string
  }
  label_list: ILabelList[]
}

export interface UpdateRatePacketaLabelParams {
  request_id: number
  return_address_id: number
  package_id: number
  productInfo: {
    weight: number
    mass_unit: string
    list: {
      return_product_id: number
      net_weight: number
      mass_unit: string
      hs_code: string
      origin_country: string
    }[]
  }
}

export interface ShippoInfoItem {
  amount_local: string
  currency_local: string
  object_id: string
  provider: string
  service_name: string
}

/** Sendcloud 费率/服务列表单项（与 getSendcloudServiceNew 等接口返回一致） */
export interface SendcloudInfoItem {
  provider: string
  service_name: string
  /** 服务标识，例如 sendcloud:letterreturn */
  shipping_method_id: string
  shipping_option_code: string
  product_code: string
  contract_id: number
  min_weight: number
  max_weight: number
  estimated_days: number
  amount_local: number
  currency_code: string
  currency_local: string
  carrierCode: string
  object_id: string
}

export interface FedexInfoItem {
  price: number
  provider: string
  service_name: string
  service_type: string
  currency: string
}

export interface PacketaInfoItem {
  amount_local: number
  currency: string
  currency_code: string
  price: number
  provider: string
  service_code: string
  service_name: string
  service_type: string
}

export interface ShipStationItem {
  carrier_code: string
  carrier_name?: string
  package_code: string
  service_code: string
  request_id: number
  return_address_id: number
}

export interface DHLReceiverAddress {
  addressHouse: string
  addressStreet: string
  city: string
  name1: string
  name2: string
  name3: string
  postalCode: string
  [property: string]: any
}

export interface DHLInfoItem {
  receiverAddress: DHLReceiverAddress
  receiverId: string
  shipperCountry: string
  [property: string]: any
}

export interface ShiprocketItem {
  amount_local: string
  courier_id: string
  currency_local: string
  name: string
  provider: string
  [property: string]: any
}

export interface SendleItem {
  name: string
  provider: string
  server: string
  amount_local: string
  currency_local: string
  currency_code: string
  [property: string]: any
}

export type ShipInfo = Record<string, ShippoInfoItem[]>
export type SendcloudInfo = Record<string, SendcloudInfoItem[]>
export type FedexInfo = FedexInfoItem[]
export type PacketaInfo = PacketaInfoItem[]
export type ShipStationInfo = ShipStationItem
export type DHLInfo = DHLInfoItem[]
export type ShiprocketInfo = ShiprocketItem[]
export type SendleInfo = SendleItem[]

export default APIManagement
